/**
 * Client analytics (§19 event contract, §12.5 consent, §15.1 no tokens/contacts in analytics).
 *
 * - Only `CLIENT_EMITTABLE_TOPICS` leave the browser; the API fills tenant, lottery, subject and serverTs.
 * - Each topic has a consent class; events whose class is not granted are dropped locally (never queued).
 *   OPERATIONAL journey events (`lottery_viewed`, `rules_expanded`, `reveal_*`, `message_button_tapped`) carry no
 *   identifiers and are on by default (D-903); MARKETING is never emitted from the browser.
 * - Payloads are sanitized: keys that look like contacts, tokens or secrets are removed and string values that
 *   look like e-mail addresses, phone numbers or social handles are redacted.
 * - Batched (≤50 per request, 5 s window), flushed with `sendBeacon` on `visibilitychange` → hidden / pagehide.
 */
import type {
  ClientEventBatchRequest,
  ClientEventInput,
  ConsentClass,
  EventPayload,
  SponsorPlacement,
} from '@windrop/contracts';
import { getApi } from './api';
import { CLIENT_TOPICS, type ClientTopic } from './api/constants';
import { getRuntimeConfig } from './config';

export type { ClientTopic };

export const TOPIC_CONSENT_CLASS: Record<ClientTopic, ConsentClass> = {
  lottery_viewed: 'OPERATIONAL',
  rules_expanded: 'OPERATIONAL',
  entry_started: 'OPERATIONAL',
  reveal_presented: 'OPERATIONAL',
  reveal_skipped: 'OPERATIONAL',
  sponsor_impression: 'SPONSOR',
  message_button_tapped: 'OPERATIONAL',
};

export type ConsentState = Record<ConsentClass, boolean>;

/** D-903: operational + aggregate sponsor delivery on by default; analytics/marketing need an explicit opt-in. */
export const DEFAULT_CONSENT: ConsentState = {
  OPERATIONAL: true,
  ANALYTICS: false,
  SPONSOR: true,
  MARKETING: false,
};

export interface AnalyticsContext {
  lotterySlug?: string;
  entryId?: string;
  campaign?: Record<string, string>;
}

export interface TrackOptions {
  placement?: SponsorPlacement;
  /** Override the topic's default class (e.g. a sponsor CTA click inside a reveal). */
  consentClass?: ConsentClass;
  clientTs?: string;
}

const FORBIDDEN_KEY =
  /(token|secret|password|authorization|cookie|session|phone|msisdn|email|e-mail|handle|contact|whatsapp|address|name$|firstname|lastname|legalname|idnumber|document)/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9][0-9 ().-]{6,}[0-9]$/;
const HANDLE_RE = /^@[A-Za-z0-9_.]{2,}$/;
const CLAIM_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;
export const REDACTED = '[redacted]';

/** Removes contact details, tokens and secrets from an event payload (recursively). Exported for tests. */
export function sanitizePayload<T>(value: T, depth = 0): T {
  if (depth > 6) return undefined as T;
  if (Array.isArray(value)) return value.map((v) => sanitizePayload(v, depth + 1)) as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (FORBIDDEN_KEY.test(k)) continue;
      out[k] = sanitizePayload(v, depth + 1);
    }
    return out as T;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    if (EMAIL_RE.test(s) || PHONE_RE.test(s) || HANDLE_RE.test(s) || CLAIM_TOKEN_RE.test(s))
      return REDACTED as T;
    return (s.length > 500 ? s.slice(0, 500) : value) as T;
  }
  return value;
}

const BATCH_MAX = 50;
const FLUSH_AFTER_MS = 5_000;

interface State {
  queue: ClientEventInput[];
  consent: ConsentState;
  context: AnalyticsContext;
  timer: ReturnType<typeof setTimeout> | null;
  installed: boolean;
  dropped: number;
}

const state: State = {
  queue: [],
  consent: { ...DEFAULT_CONSENT },
  context: {},
  timer: null,
  installed: false,
  dropped: 0,
};

export function setConsent(next: Partial<ConsentState>): void {
  state.consent = { ...state.consent, ...next };
  // Revoked consent also empties queued events of that class — nothing already accepted is sent later.
  state.queue = state.queue.filter(
    (e) => state.consent[TOPIC_CONSENT_CLASS[e.topic as ClientTopic] ?? 'ANALYTICS'],
  );
}

export function getConsent(): ConsentState {
  return { ...state.consent };
}

export function configureAnalytics(ctx: AnalyticsContext): void {
  const slugChanged = ctx.lotterySlug !== undefined && ctx.lotterySlug !== state.context.lotterySlug;
  if (slugChanged && state.queue.length) void flush();
  state.context = { ...state.context, ...ctx };
}

function schedule(): void {
  if (state.timer || typeof setTimeout === 'undefined') return;
  state.timer = setTimeout(() => {
    state.timer = null;
    void flush();
  }, FLUSH_AFTER_MS);
}

/** Queue one event. Returns false when it was dropped (consent, unknown topic, analytics disabled). */
export function track<T extends ClientTopic>(
  topic: T,
  payload: EventPayload<T>,
  opts: TrackOptions = {},
): boolean {
  if (!getRuntimeConfig().analyticsEnabled) return false;
  if (!(CLIENT_TOPICS as readonly string[]).includes(topic)) return false;
  const cls = opts.consentClass ?? TOPIC_CONSENT_CLASS[topic];
  if (!state.consent[cls]) {
    state.dropped++;
    return false;
  }
  const event: ClientEventInput = {
    topic,
    clientTs: opts.clientTs ?? new Date().toISOString(),
    payload: sanitizePayload(payload as Record<string, unknown>),
    placement: opts.placement,
    campaign:
      state.context.campaign && Object.keys(state.context.campaign).length
        ? state.context.campaign
        : undefined,
  };
  state.queue.push(event);
  if (state.queue.length >= BATCH_MAX) void flush();
  else schedule();
  return true;
}

/** Sends everything queued. `beacon: true` on page hide uses `navigator.sendBeacon`. Never throws. */
export async function flush(opts: { beacon?: boolean } = {}): Promise<void> {
  if (state.timer) {
    clearTimeout(state.timer);
    state.timer = null;
  }
  const slug = state.context.lotterySlug;
  if (!state.queue.length || !slug) return; // events without a lottery context wait for configureAnalytics
  const events = state.queue.splice(0, BATCH_MAX);
  const body: ClientEventBatchRequest = { lotterySlug: slug, entryId: state.context.entryId, events };
  try {
    await getApi().postAnalyticsEvents(body, { beacon: opts.beacon });
  } catch {
    /* best-effort */
  }
  if (state.queue.length) await flush(opts);
}

/** Registers the page-hide flush. Idempotent. */
export function installAnalytics(): () => void {
  if (state.installed || typeof document === 'undefined') return () => {};
  state.installed = true;
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') void flush({ beacon: true });
  };
  const onPageHide = () => void flush({ beacon: true });
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    state.installed = false;
  };
}

/** Test hook. */
export function resetAnalytics(): void {
  if (state.timer) clearTimeout(state.timer);
  state.queue = [];
  state.timer = null;
  state.consent = { ...DEFAULT_CONSENT };
  state.context = {};
  state.dropped = 0;
}

/** Test hook: a snapshot of what is queued. */
export function pendingEvents(): readonly ClientEventInput[] {
  return state.queue.slice();
}
